import React, { useCallback, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Alert,
  LinearProgress,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Visibility as PreviewIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';

interface ImageUploadProps {
  onImageChange: (file: File | File[] | null) => void;
  accept?: string;
  maxSize?: number;
  multiple?: boolean;
  preview?: boolean;
  maxFiles?: number;
  existingImages?: File[];
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  onImageChange,
  accept = 'image/*',
  maxSize = 5 * 1024 * 1024, // 5MB
  multiple = false,
  preview = true,
  maxFiles = 10,
  existingImages = [],
}) => {
  const [images, setImages] = useState<File[]>(existingImages);
  const [previewImage, setPreviewImage] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: any[]) => {
    if (rejectedFiles.length > 0) {
      console.error('Arquivos rejeitados:', rejectedFiles);
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    // Simular upload com progresso
    const uploadInterval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 100) {
          clearInterval(uploadInterval);
          setUploading(false);
          
          // Adicionar imagens
          const newImages = multiple ? [...images, ...acceptedFiles] : acceptedFiles;
          setImages(newImages);
          
          // Notificar componente pai
          if (multiple) {
            onImageChange(newImages);
          } else {
            onImageChange(acceptedFiles[0] || null);
          }
          
          return 100;
        }
        return prev + 10;
      });
    }, 100);
  }, [images, multiple, onImageChange]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
    },
    maxSize,
    multiple,
    maxFiles,
  });

  const handleRemoveImage = (index: number) => {
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
    
    if (multiple) {
      onImageChange(newImages);
    } else {
      onImageChange(null);
    }
  };

  const handlePreviewImage = (image: File) => {
    setPreviewImage(image);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box>
      {/* Área de upload */}
      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'grey.300',
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
          bgcolor: isDragActive ? 'action.hover' : 'background.paper',
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: 'action.hover',
          },
        }}
      >
        <input {...getInputProps()} />
        
        <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        
        {isDragActive ? (
          <Typography variant="h6" color="primary">
            Solte as imagens aqui...
          </Typography>
        ) : (
          <Box>
            <Typography variant="h6" gutterBottom>
              Arraste e solte imagens aqui
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              ou clique para selecionar arquivos
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Formatos aceitos: JPEG, PNG, GIF, WebP (máx. {formatFileSize(maxSize)})
              {multiple && ` • Máx. ${maxFiles} arquivos`}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Progresso de upload */}
      {uploading && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" gutterBottom>
            Enviando imagens...
          </Typography>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="caption" color="text.secondary">
            {uploadProgress}%
          </Typography>
        </Box>
      )}

      {/* Preview das imagens */}
      {preview && images.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Imagens ({images.length})
          </Typography>
          
          <Grid container spacing={2}>
            {images.map((image, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card>
                  <CardMedia
                    component="img"
                    height="200"
                    image={URL.createObjectURL(image)}
                    alt={image.name}
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardContent sx={{ p: 1 }}>
                    <Typography variant="body2" noWrap>
                      {image.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(image.size)}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ p: 1, justifyContent: 'space-between' }}>
                    <IconButton
                      size="small"
                      onClick={() => handlePreviewImage(image)}
                      color="primary"
                    >
                      <PreviewIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveImage(index)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Informações e dicas */}
      <Box sx={{ mt: 2 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Dicas para imagens:</strong><br />
            • Use imagens de alta qualidade (mín. 800x600px)<br />
            • Formatos WebP são recomendados para melhor performance<br />
            • Mantenha o tamanho dos arquivos otimizado<br />
            • Use imagens com boa iluminação e foco
          </Typography>
        </Alert>
      </Box>

      {/* Dialog de preview */}
      <Dialog
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Preview da Imagem
          {previewImage && (
            <Typography variant="body2" color="text.secondary">
              {previewImage.name} • {formatFileSize(previewImage.size)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {previewImage && (
            <Box sx={{ textAlign: 'center' }}>
              <img
                src={URL.createObjectURL(previewImage)}
                alt={previewImage.name}
                style={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewImage(null)}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ImageUpload;
